import { FlexRow } from '@/components/common/Flex';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box, BoxProps, InputGroup } from '@chakra-ui/react';
import { dt } from '@common/util/dateTime';
import { isValidDateString } from '@common/validation';
import { CalendarDate, DateValue, parseDate } from '@internationalized/date';
import { useRef } from 'react';
import { AriaDatePickerProps, useDatePicker } from 'react-aria';
import { useDatePickerState } from 'react-stately';
import { Calendar } from './Calendar';
import { Dialog } from './Dialog';
import { FieldButton } from './FieldButton';
import { Popover } from './Popover';

interface InlineDatePickerProps extends Omit<BoxProps, 'onChange'> {
    label?: string | React.ReactNode;
    value?: string;
    onChange: (value: string) => void;
    options?: AriaDatePickerProps<CalendarDate>;
}

const INLINE_DATE_PICKER_ID = 'inline-date-picker';

const InlineDatePicker = ({
    label,
    value,
    onChange,
    options,
    ...props
}: InlineDatePickerProps) => {
    const state = useDatePickerState({
        value: isValidDateString(value) ? parseDate(value) : undefined,
        onChange: (value) => {
            if (value) onChange(value.toString());
        },
    });
    const ref = useRef(null);
    const { groupProps, fieldProps, buttonProps, dialogProps, calendarProps } =
        useDatePicker<CalendarDate>(
            { 'aria-label': 'Date', ...options },
            state,
            ref
        );

    const formatValue = (value: DateValue | null | undefined): string =>
        value ? dt(value.toString()).fromNowDays() : dt().fromNowDays();

    return (
        <FlexRow {...props}>
            <FlexRow fontSize="sm" color="gray.500" mb={{ base: 1, '2xl': 2 }}>
                {label}
            </FlexRow>
            <Box display="inline-block">
                <input type="hidden" value={value} />
                {/* Chakra's type-mapping is a bit wonky here. We're only using a few of these types, and know they play nicely. */}
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <InputGroup
                    {...groupProps}
                    ref={ref}
                    id={INLINE_DATE_PICKER_ID}
                >
                    <FieldButton
                        {...buttonProps}
                        sx={{
                            display: 'inline-block',
                            marginLeft: 1,
                            fontSize: 12,
                            height: 'auto',
                            position: 'relative',
                            color: 'gray.600',
                        }}
                    >
                        {formatValue(fieldProps.value)}
                        <ChevronDownIcon />
                    </FieldButton>
                </InputGroup>
                {state.isOpen && (
                    <Popover
                        triggerRef={ref}
                        state={state}
                        placement="bottom start"
                    >
                        <Dialog {...dialogProps}>
                            <Calendar {...calendarProps} />
                        </Dialog>
                    </Popover>
                )}
            </Box>
        </FlexRow>
    );
};

export default InlineDatePicker;
